import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AbstractFeatureService } from '@y42/feature';
import { METADATA_JSON_SCHEMA_URL, Y42MetadataGitModel } from '@y42/feature/json-schema';
import { Y42RouterService, Y42Routes } from '@y42/feature/routing';
import { GitQuery } from '@y42/git';
import { Y42FeatureType } from '@y42/json-schema';
import { ToastService } from '@y42/toast';
import { getSafeString } from '@y42/util/safe-string';
import {
  CreateDataLineageParams,
  DataLineageAclService,
  DataLineageGitService,
  DataLineageQuery,
  DataLineageState,
  DataLineageStore,
  DataLineageStoreModel,
} from '../../state/data-lineage';
import { DataLineageUserDataService } from './data-lineage-user-data.service';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class DataLineageService extends AbstractFeatureService<DataLineageState> {
  constructor(
    protected readonly userData: DataLineageUserDataService,
    protected readonly store: DataLineageStore,
    protected readonly query: DataLineageQuery,
    protected readonly git: DataLineageGitService,
    protected readonly acl: DataLineageAclService,
    protected readonly routerService: Y42RouterService,
    protected readonly toast: ToastService,
    protected readonly gitQuery: GitQuery,
  ) {
    super({
      featureType: Y42FeatureType.Lineage,
      git,
      acl,
      store,
      query,
      userData,
      gitQuery,
      toast,
      childStores: [],
    });
  }

  async create({ title, description, lineageDiagramUIModel, selectedFilters }: CreateDataLineageParams): Promise<void> {
    const $name = getSafeString(title);
    const snapshot = this.store.create({
      ...new Y42MetadataGitModel(),
      $schema: METADATA_JSON_SCHEMA_URL,
      $name,
      name: title,
      description,
      ...lineageDiagramUIModel,
      selectedFilters,
    });

    await this.store.pending;
    await this.git.commitChanges(`lineage: new snapshot "${snapshot.name}" created`, snapshot.$path);
    await this.routerService.navigateByUrl(`${this.routerService.getURLTo(Y42Routes.Lineage)}/${$name}`);
  }

  async update(
    item: Partial<DataLineageStoreModel>,
    $path: DataLineageStoreModel['$path'] = this.query.getActiveId()!,
  ): Promise<void> {
    if (!$path) {
      throw new Error(`DataLineage ${$path} not found.`);
    }

    this.store.update($path, item);
    await this.store.pending;
  }

  async delete($path = this.query.getActiveId()!): Promise<void> {
    const snapshot = this.query.getEntity($path);
    await this.store.delete($path);
    await this.store.pending;
    await this.git.commitChanges(`lineage: snapshot "${snapshot?.$name ?? $path}" deleted.`, $path);
    await this.routerService.navigateByUrl(`${this.routerService.getURLTo(Y42Routes.Lineage)}`);
  }

  async commitChanges(model: DataLineageStoreModel) {
    const modifiedFiles = this.gitQuery.getModifiedFilesList().filter((filePath) => filePath.startsWith(model.$path));
    if (modifiedFiles.length > 0) {
      await this.git.commitChanges(`Lineage ${model.$name}: updated`, modifiedFiles);
    }
  }
}
